/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/admin/adminNav.ts
 *
 * Rôle :
 *   Source de vérité UNIQUE de la navigation du backoffice (coquille
 *   AdminShell). Décrit les ~27 sections admin groupées, leur route,
 *   leur libellé i18n, leur pictogramme, le(s) rôle(s) backoffice
 *   autorisé(s) (matrice §10.16.6) et un éventuel feature flag de
 *   visibilité (Chantier B, couper une section depuis /admin/feature-flags).
 *
 *   Cette config est consommée par `AdminShell` (rendu sidebar +
 *   filtrage par rôle + filtrage par flag) et par les tests Vitest
 *   (vérifie que chaque route câblée dans App.tsx a une entrée de menu).
 *
 * Réutilisable :
 *   100 %. Aucun couplage runtime, c'est de la donnée déclarative.
 *   Pour un autre salon DRANE : remplacer les items.
 *
 * Voir : CLAUDE.md §21 (CMS + rôles backoffice étendus),
 *        HANDOFF-2026-05-17-22-45.md §2.A.
 *
 * @author Mika
 * @since  2026-05-17
 */

/** Rôles backoffice (enum V2 + V36, cf. CLAUDE.md §21 + §5 2026-05-20). */
export type AdminRole =
  | 'super_admin'
  | 'admin'
  | 'editeur'
  | 'auteur'
  | 'moderateur'
  | 'staff'
  // FIX 2026-05-20 (Message 6 Mika, feature complète VIP) : rôle
  // backoffice restreint avec accès UNIQUEMENT à /admin/vip-invitations.
  // Ajouté à l'ENUM SQL via V36__add_vip_manager_role.sql.
  | 'vip_manager';

/** Tous les rôles backoffice qui voient le panneau d'admin général. */
export const ALL_ADMIN_ROLES: readonly AdminRole[] = [
  'super_admin',
  'admin',
  'editeur',
  'auteur',
  'moderateur',
  'staff',
] as const;

/** Sections strictement réservées au super_admin (cf. App.tsx). */
export const SUPER_ADMIN_ONLY: readonly AdminRole[] = ['super_admin'] as const;

/**
 * FIX 2026-05-20 : Rôles autorisés sur /admin/vip-invitations.
 * Inclut tous les ADMIN_ROLES + le rôle restreint `vip_manager` qui ne
 * voit QUE cette page (toutes les autres entrées du menu utilisent
 * `ALL_ADMIN_ROLES` qui n'inclut PAS `vip_manager` → masquage de la
 * navigation côté UI). Sécurité réelle = côté serveur dans
 * `_lib/auth.php::auth_check_admin` (acceptation explicite).
 */
export const VIP_MANAGER_ALLOWED: readonly AdminRole[] = [
  ...ALL_ADMIN_ROLES,
  'vip_manager',
] as const;

/** Une entrée de menu = une section admin. */
export interface AdminNavItem {
  /** Chemin React Router (doit exister dans App.tsx). */
  readonly to: string;
  /** Clé i18n du libellé court de la sidebar. */
  readonly labelKey: string;
  /** Pictogramme emoji (cohérent BottomNav/PlusPage, pas de lib d'icônes). */
  readonly icon: string;
  /**
   * Rôles backoffice autorisés. Doit MIROITER le RoleGuard de la route
   * dans App.tsx (sécurité réelle = serveur + RoleGuard ; ici = UX, on
   * masque ce que le rôle ne peut atteindre).
   */
  readonly roles: readonly AdminRole[];
  /**
   * Feature flag de visibilité (Chantier B). Si défini ET explicitement
   * désactivé au backoffice, l'entrée disparaît de la sidebar. Absent ou
   * inconnu → visible (défaut ON, jamais de menu cassé).
   */
  readonly flag?: string;
  /** Sous-chemins qui marquent aussi l'item actif (ex. /admin/pages/:id). */
  readonly matchPrefixes?: readonly string[];
  /**
   * DROIT VIRTUEL (2026-08-26), une capacité, pas une page.
   * `virtual: true` ⇒ absent de la sidebar, mais attribuable dans
   * /admin/access, par rôle ET par personne.
   */
  readonly virtual?: boolean;
  /**
   * Cette section est un ONGLET d'un autre ecran (2026-08-26) : elle garde sa
   * route et reste attribuable dans /admin/access, seule son entree de barre
   * laterale disparait. A distinguer de `virtual`, qui n'a aucune route.
   */
  readonly tabOf?: string;
  /**
   * 2026-09-30 (dossier exposants et animateurs) : `roles` n'est ici que le
   * DEFAUT. Une surcharge accordee dans /admin/access (par compte ou par
   * role) suffit a faire apparaitre l'entree, meme pour un role absent de
   * `roles`. Sans ce drapeau, le filtre de role de la barre laterale masquait
   * l'entree a la personne a qui on venait d'accorder le droit.
   * La decision revient alors entierement a `resolveAllowed` (AdminShell).
   */
  readonly grantable?: boolean;
}

/** Un groupe de la sidebar (titre + items). */
export interface AdminNavGroup {
  /** Clé i18n du titre de groupe. */
  readonly titleKey: string;
  readonly items: readonly AdminNavItem[];
}

/**
 * Navigation complète du backoffice, groupée selon HANDOFF §2.A.
 * Les `roles` reflètent EXACTEMENT les RoleGuard de App.tsx.
 */
export const ADMIN_NAV: readonly AdminNavGroup[] = [
  // 2026-07-07 (Mika, cockpit « Étape du salon »), pilotage UNIQUE de
  // l'ouverture/fermeture du salon (presets d'étapes + verrous métier),
  // en tête de sidebar. Lisible par tout admin ; les écritures sont
  // gardées super_admin côté serveur (api/admin/pilotage.php).
  {
    titleKey: 'adminNav.group.pilotage',
    items: [
      {
        to: '/admin/pilotage',
        labelKey: 'adminNav.pilotage',
        icon: '🚦',
        roles: ALL_ADMIN_ROLES,
        // Pas de flag : le pilotage doit rester joignable en permanence
        // (on ne veut jamais couper l'écran qui ouvre/ferme le salon).
      },
    ],
  },
  {
    titleKey: 'adminNav.group.dashboard',
    items: [
      {
        to: '/admin/dashboard',
        labelKey: 'adminNav.dashboard',
        icon: '📊',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.dashboard',
      },
      // 2026-10-04 (Mika), « Demandez à l’événement » : une question en
      // français, une réponse chiffrée tirée d’une liste fermée d’analyses
      // (api/admin/demandez-au-salon.php). Ouverte par défaut au
      // super-administrateur, à l’administrateur et au modérateur (Laurent),
      // attribuable ou retirable dans /admin/access (grantable) ; la V145
      // pose aussi la surcharge du rôle modérateur. Le serveur applique la
      // même règle (demandez_autorise). Mikael - chef de projet - 2026-10-04
      {
        to: '/admin/demandez',
        labelKey: 'adminNav.demandez',
        icon: '💬',
        roles: ['super_admin', 'admin', 'moderateur'],
        grantable: true,
      },
      // G1 (2026-06-13 (Sprint I, parité admin 2025)), dashboard
      // analytique EMPLOI : KPIs emploi, entonnoir de conversion, top
      // exposants par candidats, répartitions secteur/profil/expérience,
      // affluence horaire, tendances 7 j + générateur de rapport (CSV/PDF).
      // Branché sur le VRAI endpoint api/admin/analytics-emploi.php.
      {
        to: '/admin/emploi-analytics',
        labelKey: 'adminNav.emploiAnalytics',
        icon: '📈',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.emploi_analytics',
      },
      // FIX 2026-05-22 (sprint SCANS-TRACKING-STATS, ST1), Mode TV
      // plein écran pour projection PC central jour J. Page hors
      // AdminShell : la sidebar n'est PAS rendue dessus, mais on
      // expose tout de même un raccourci ici pour la lancer.
      {
        to: '/admin/dashboard/wall',
        labelKey: 'adminNav.dashboardWall',
        icon: '📺',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.dashboard_wall',
      },
      // 2026-10-04 (Mika), la présentation qui défile (16:9, diffusion non
      // tactile) : « Laurent doit pouvoir l'ouvrir et la diffuser avec son
      // compte ». Ouverte par défaut au modérateur (et à l'administrateur),
      // attribuable à tout compte dans /admin/access (grantable) ; la V144
      // pose aussi la surcharge du rôle modérateur, visible dans l'éditeur.
      // Page hors AdminShell : sa garde de section est SectionGuard.
      {
        to: '/admin/presentation',
        labelKey: 'adminNav.presentation',
        icon: '🎞️',
        roles: ['super_admin', 'admin', 'moderateur'],
        grantable: true,
      },
      // FIX 2026-05-22 (sprint SCANS-TRACKING-STATS, T1), Heatmap
      // densité cumulée des check-ins sur le plan SVG.
      {
        to: '/admin/heatmap',
        labelKey: 'adminNav.heatmap',
        icon: '🔥',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.heatmap',
      },
      // FIX 2026-05-23 (sprint « finir 100 % », S10), recalibrage GPS
      // des stands depuis les scans géolocalisés (capture opt-in V45).
      {
        to: '/admin/stand-positions',
        labelKey: 'adminNav.standPositions',
        icon: '📍',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.stand_positions',
      },
      // FIX 2026-05-22 (sprint SCANS-TRACKING-STATS, T3+T4), Alertes
      // staff temps réel (mineurs perdus, saturation salon/atelier).
      // ALL_ADMIN_ROLES car opérationnel jour J (staff doit pouvoir
      // acquitter sans attendre un super_admin).
      {
        to: '/admin/alerts',
        labelKey: 'adminNav.staffAlerts',
        icon: '🚨',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.staff_alerts',
      },
      // FIX 2026-05-22 (sprint SCANS-TRACKING-STATS, ST8), Bilan PDF
      // jour J HTML imprimable. Page utilisée par les super_admins qui
      // reçoivent le lien par mail à 18h via le cron daily-report.php.
      {
        to: '/admin/daily-report',
        labelKey: 'adminNav.dailyReport',
        icon: '📄',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.daily_report',
      },
      // 2026-09-10 (chantier bilan d impact), la vue unique destinee a un
      // financeur : inscriptions par type de public, structures, offres,
      // passages, activites, et la liste de ce qui n est PAS mesure. Placee
      // apres le bilan du jour J, qui repond a une autre question : celui-ci
      // decrit l evenement entier, pas la journee.
      // Pas de flag de visibilite : c est l ecran qu on cherche justement le
      // jour ou il faut deposer un dossier, on ne veut pas qu il ait pu etre
      // coupe six mois plus tot sans que personne s en souvienne.
      {
        to: '/admin/bilan',
        labelKey: 'adminNav.bilan',
        icon: '🧾',
        roles: ALL_ADMIN_ROLES,
      },
    ],
  },
  {
    titleKey: 'adminNav.group.workshops',
    items: [
      {
        to: '/admin/workshops',
        labelKey: 'adminNav.workshops',
        icon: '🧩',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.workshops',
      },
      {
        to: '/admin/workshop-attendance',
        labelKey: 'adminNav.workshopAttendance',
        icon: '✅',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.workshop_attendance',
      },
      {
        to: '/admin/signups',
        labelKey: 'adminNav.signups',
        icon: '📝',
        roles: ['super_admin', 'admin', 'editeur', 'auteur', 'staff', 'moderateur'],
        flag: 'admin.signups',
      },
      {
        to: '/admin/programs',
        labelKey: 'adminNav.programs',
        icon: '🗂️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.programs',
      },
    ],
  },
  {
    titleKey: 'adminNav.group.content',
    items: [
      {
        to: '/admin/pages',
        labelKey: 'adminNav.pages',
        icon: '📄',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.pages',
        matchPrefixes: ['/admin/pages'],
      },
      {
        to: '/admin/medias',
        labelKey: 'adminNav.medias',
        icon: '🎬',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.medias',
      },
      {
        to: '/admin/media-streams',
        labelKey: 'adminNav.mediaStreams',
        icon: '📡',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.media_streams',
      },
      {
        to: '/admin/partners',
        labelKey: 'adminNav.partners',
        icon: '🤝',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.partners',
      },
      // FIX 2026-05-18, zone « Organisateur » (clone Partenaires, V29).
      {
        to: '/admin/organisateurs',
        labelKey: 'adminNav.organisateurs',
        icon: '🎗️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.organisateurs',
      },
      {
        to: '/admin/tags',
        labelKey: 'adminNav.tags',
        icon: '🏷️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.tags',
      },
      // NOLAS 2026 (Vague 1 / LOT-H (Contenu éditorial, M7)), éditeur du
      // contenu accueil/À propos/footer (settings content.*, V67). Miroir
      // du contrôle serveur : super_admin/admin. flag défaut ON.
      {
        to: '/admin/content',
        labelKey: 'adminNav.content',
        icon: '📝',
        roles: ['super_admin', 'admin'],
        flag: 'admin.content',
      },
      {
        to: '/admin/translations',
        labelKey: 'adminNav.translations',
        icon: '🌐',
        roles: ['super_admin', 'admin', 'editeur', 'auteur', 'moderateur', 'staff'],
        flag: 'admin.translations',
      },
      {
        to: '/admin/navigation',
        labelKey: 'adminNav.navigation',
        icon: '🧭',
        roles: ['super_admin', 'admin', 'editeur', 'auteur', 'moderateur', 'staff'],
        flag: 'admin.navigation',
      },
      // NOLAS 2026 (Vague 1 / LOT-G (Sondages, M8)), CRUD de sondages
      // ciblés par profil candidat + restitution/export. Miroir du
      // contrôle serveur : super_admin/admin/editeur. flag défaut ON.
      {
        to: '/admin/surveys',
        labelKey: 'adminNav.surveys',
        icon: '🗳️',
        roles: ['super_admin', 'admin', 'editeur'],
        flag: 'admin.surveys',
      },
    ],
  },
  {
    titleKey: 'adminNav.group.users',
    items: [
      {
        to: '/admin/users-admin',
        labelKey: 'adminNav.usersAdmin',
        icon: '👥',
        roles: SUPER_ADMIN_ONLY,
        flag: 'admin.users_admin',
      },
      // Lot << 2FA au choix >>, porte sur NOLAS le 2026-09-27 (ecrit le
      // 2026-08-03). Ecran d ADMINISTRATION : qui utilise quoi, qui depend
      // d un telephone, qui n a presque plus de codes de secours, et le
      // retour en 2FA par courriel pour debloquer quelqu un.
      //
      // Pas de drapeau de visibilite : couper cet ecran reviendrait a retirer
      // le seul recours de deblocage d un compte, meme raisonnement que
      // /admin/maintenance (anti-verrouillage).
      {
        to: '/admin/securite-comptes',
        labelKey: 'adminNav.twoFactorAdmin',
        icon: '🛡️',
        roles: ['super_admin', 'admin'],
      },
      {
        to: '/admin/invitations',
        labelKey: 'adminNav.invitations',
        icon: '✉️',
        roles: SUPER_ADMIN_ONLY,
        flag: 'admin.invitations',
      },
      {
        to: '/admin/exhibitors',
        labelKey: 'adminNav.exhibitors',
        icon: '🏢',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.exhibitors',
      },
      // 2026-09-20 (Mika), toutes les offres d'emploi sur un ecran, avec
      // exports XLSX et PDF. Attribuable dans /admin/access, comme la fiche
      // complete : le serveur (api/admin/jobs.php) porte la vraie garde.
      {
        to: '/admin/jobs',
        labelKey: 'adminNav.jobs',
        icon: '💼',
        roles: ALL_ADMIN_ROLES,
      },
      // 2026-09-27 (Mika), les CV deposes sur un seul ecran, avec le lien qui
      // ouvre le document. Attribuable dans /admin/access comme la fiche
      // complete ; le serveur (api/admin/cvs.php) porte la vraie garde, et
      // exige en plus le droit de voir les coordonnees.
      {
        to: '/admin/cvs',
        labelKey: 'adminNav.cvs',
        icon: '📄',
        roles: ALL_ADMIN_ROLES,
      },
      // 2026-09-30 (Mika), le dossier exposants et animateurs : un ZIP avec
      // un dossier par exposant et par animateur. Super-administrateur par
      // defaut ; accordable par compte (Laurent) ou par role dans
      // /admin/access. Le serveur (api/admin/export-dossier.php) porte la
      // vraie garde, aux deux actions.
      {
        to: '/admin/export-dossier',
        labelKey: 'adminNav.exportDossier',
        icon: '🗂️',
        roles: SUPER_ADMIN_ONLY,
        grantable: true,
      },
      // NOLAS 2026, M5 / G7 (Mika 2026-06-14), import exposants CSV.
      // Création de comptes + génération de codes → super_admin + admin.
      {
        to: '/admin/exhibitors-import',
        labelKey: 'adminNav.exhibitorsImport',
        icon: '📥',
        roles: ['super_admin', 'admin'],
        flag: 'admin.exhibitors',
      },
      // FIX 2026-05-19 (demande Mika), tous les comptes inscrits par
      // rôle/type + révocation d'accès.
      {
        to: '/admin/users',
        labelKey: 'adminNav.usersList',
        icon: '🧑‍🤝‍🧑',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.users_list',
      },
      // M5/G5 (2026-06-14, parité admin 2025), badges visiteurs en lot
      // (impression au desk d'accueil jour J). Tout rôle backoffice.
      {
        to: '/admin/visitor-badges',
        labelKey: 'adminNav.visitorBadges',
        icon: '🪪',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.visitor_badges',
      },
      // FIX 2026-05-26 (Mika valorisation pro), export CSV
      // consolidé QUI/QUAND/OÙ pour les attestations de participation.
      // Restreint super_admin + admin (données nominatives en clair).
      {
        to: '/admin/attendance-export',
        labelKey: 'adminNav.attendanceExport',
        icon: '📋',
        roles: ['super_admin', 'admin'],
        flag: 'admin.attendance_export',
      },
      {
        // 2026-08-26, ecran « Animateurs », pendant de « Exposants ».
        to: '/admin/workshop-hosts',
        labelKey: 'adminNav.workshopHosts',
        icon: '🎤',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.workshops',
      },
      {
        // 2026-08-26 (Mika), « que je puisse facilement recuperer tout cela
        // dans le backoffice » : les pieces des options de visibilite,
        // rassemblees en une liste au lieu d'etre eparpillees dans 23 fiches.
        to: '/admin/visibilite',
        labelKey: 'adminNav.visibility',
        icon: '📣',
        roles: ALL_ADMIN_ROLES,
      },
      {
        to: '/admin/exhibitor-code-requests',
        labelKey: 'adminNav.exhibitorCodeRequests',
        icon: '📥',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.exhibitors',
        tabOf: '/admin/exhibitors',
      },
      // FIX 2026-05-19 (spec §5), codes EXPO (exposant/organisateur).
      {
        to: '/admin/exhibitor-codes',
        labelKey: 'adminNav.exhibitorCodes',
        icon: '🎟️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.exhibitor_codes',
      },
      // 2026-06-23 (Mika, Phase 1 onboarding dual), modèles d'e-mail
      // transactionnels (exhibitor_code / workshop_host_code), envoyés à
      // l'approbation d'une demande de code. Même domaine que les codes EXPO.
      {
        to: '/admin/email-templates',
        labelKey: 'adminNav.emailTemplates',
        icon: '📧',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.exhibitor_codes',
      },
      // FIX 2026-05-20 (Option A, invitations VIP par e-mail §5/§24)
      // Flux complet : admin crée → token HMAC forgé → e-mail envoyé →
      // VIP clique le lien → activation. Cf. _lib/vip-invite.php +
      // api/admin/vip-invitations.php. Partage le flag exhibitor_codes
      // (même classe « gestion des invitations »).
      //
      // FIX 2026-05-20 (Message 6 Mika) : roles = VIP_MANAGER_ALLOWED →
      // accepte tous les admins + le rôle restreint `vip_manager` qui
      // ne voit QUE cette page dans le backoffice (toutes les autres
      // entrées restent ALL_ADMIN_ROLES sans vip_manager).
      {
        to: '/admin/vip-invitations',
        labelKey: 'adminNav.vipInvitations',
        icon: '🎖️',
        roles: VIP_MANAGER_ALLOWED,
        flag: 'admin.exhibitor_codes',
      },
      // FIX 2026-05-20 (Message 7 Mika), templates de mail VIP éditables.
      // Le rôle vip_manager DOIT pouvoir gérer ses templates (« le profil
      // gestion VIP doit pouvoir tout faire »). roles = VIP_MANAGER_ALLOWED.
      {
        to: '/admin/vip-email-templates',
        labelKey: 'adminNav.vipEmailTemplates',
        icon: '✉️',
        roles: VIP_MANAGER_ALLOWED,
        flag: 'admin.exhibitor_codes', // partage le flag (même domaine fonctionnel)
      },
      {
        to: '/admin/groups',
        labelKey: 'adminNav.groups',
        icon: '👨‍👩‍👧‍👦',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.groups',
      },
      {
        to: '/admin/user-journey',
        labelKey: 'adminNav.userJourney',
        icon: '🧬',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.user_journey',
      },
    ],
  },
  {
    titleKey: 'adminNav.group.forms',
    items: [
      {
        to: '/admin/roles',
        labelKey: 'adminNav.roles',
        icon: '🪪',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.roles',
      },
      {
        to: '/admin/role-programs',
        labelKey: 'adminNav.rolePrograms',
        icon: '🗂️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.role_programs',
      },
      {
        to: '/admin/form-builder',
        labelKey: 'adminNav.formBuilder',
        icon: '🧱',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.form_builder',
      },
      // Lot CIRFA (2026-08-03), les indicateurs se lisent juste à côté de
      // l'écran où l'on définit les champs : on ajoute un champ, on vient voir
      // ce qu'il donne. Même flag de section que le générateur, car c'est la
      // même responsabilité (qui règle le formulaire en lit les réponses).
      {
        to: '/admin/form-stats',
        labelKey: 'adminNav.formStats',
        icon: '📊',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.form_builder',
      },
    ],
  },
  {
    // Lot CIRFA (2026-08-03), groupe « Accueil des groupes ». Les deux écrans
    // servent le même moment : préparer l'arrivée d'un car de lycéens sur un
    // site où l'on contrôle les identités. Les séparer dans deux rubriques
    // éloignées obligerait à faire des allers-retours le jour J.
    titleKey: 'adminNav.group.groupsWelcome',
    items: [
      {
        to: '/admin/listes-nominatives',
        labelKey: 'adminNav.groupRoster',
        icon: '📋',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.group_roster',
      },
      {
        to: '/admin/transport',
        labelKey: 'adminNav.transport',
        icon: '🚌',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.transport',
      },
    ],
  },
  {
    titleKey: 'adminNav.group.communication',
    items: [
      {
        to: '/admin/push',
        labelKey: 'adminNav.push',
        icon: '🔔',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.push',
      },
      // M5/G3 (2026-06-14, parité admin 2025), composeur d'e-mails de masse
      // (Tous/Candidats/Exposants/Organisateurs). super_admin + admin (envoi
      // sensible). Flag admin.email_composer (défaut ON).
      {
        to: '/admin/email-composer',
        labelKey: 'adminNav.emailComposer',
        icon: '📧',
        roles: ['super_admin', 'admin'],
        flag: 'admin.email_composer',
      },
      // M5/G4 (2026-06-14, parité admin 2025), bilan quotidien par exposant
      // (cron 18h envoie à chaque exposant son bilan). super_admin + admin.
      {
        to: '/admin/exhibitor-reports',
        labelKey: 'adminNav.exhibitorReports',
        icon: '📨',
        roles: ['super_admin', 'admin'],
        flag: 'admin.exhibitor_reports',
      },
    ],
  },
  {
    titleKey: 'adminNav.group.config',
    items: [
      {
        to: '/admin/settings',
        labelKey: 'adminNav.settings',
        icon: '⚙️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.settings',
      },
      // 2026-07-07 (Mika, V76), contrôle d'accès admin par section
      // (profils de rôle éditables + surcharge par compte). super_admin only.
      {
        to: '/admin/access',
        labelKey: 'adminNav.access',
        icon: '🔐',
        roles: SUPER_ADMIN_ONLY,
        // Pas de flag : l'écran qui gère les accès ne doit pas pouvoir se
        // couper lui-même (anti-lockout, comme feature-flags).
      },
      // FIX 2026-05-18, choix des 6 tuiles de la page d'accueil
      // (Partenaires OU Organisateur, etc.) : RoleGuard admin+.
      {
        to: '/admin/home-tiles',
        labelKey: 'adminNav.homeTiles',
        icon: '🏠',
        roles: ['super_admin', 'admin'],
        flag: 'admin.home_tiles',
      },
      // 2026-09-20 (Mika), format et contenu du badge imprime : feuille A4
      // pliee, elements, verso (affiche), couleurs par profil.
      {
        to: '/admin/badge-settings',
        labelKey: 'adminNav.badgeSettings',
        icon: '🪪',
        roles: ['super_admin', 'admin'],
      },
      // 2026-09-20 (CV Matching, vague 1) : le module premium, ses plafonds,
      // la plage des entretiens, les salles. A cote des badges, dans la
      // configuration : c est un reglage d instance, pas une operation.
      {
        to: '/admin/premium-settings',
        labelKey: 'adminNav.premiumSettings',
        icon: '⭐',
        roles: ['super_admin', 'admin'],
      },
      // 2026-09-20 (CV Matching, vague 3) : tous les entretiens des entreprises
      // premium, l occupation des salles, la reattribution, l export. A cote
      // des reglages premium, dont il depend (salles, plage horaire).
      {
        to: '/admin/interviews',
        labelKey: 'adminNav.interviews',
        icon: '🗓️',
        roles: ['super_admin', 'admin'],
      },
      {
        to: '/admin/feature-flags',
        labelKey: 'adminNav.featureFlags',
        icon: '🎚️',
        roles: ALL_ADMIN_ROLES,
        // Pas de flag : c'est l'écran qui pilote les flags, il ne doit
        // jamais pouvoir se couper lui-même (anti-lockout).
      },
      {
        to: '/admin/ai-config',
        labelKey: 'adminNav.aiConfig',
        icon: '🧠',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.ai_config',
      },
      {
        to: '/admin/event-config',
        labelKey: 'adminNav.eventConfig',
        icon: '🗓️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.event_config',
      },
      // FIX 2026-05-19 (spec §6), types de contrôle / émargement.
      {
        to: '/admin/control-types',
        labelKey: 'adminNav.controlTypes',
        icon: '🚪',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.control_types',
      },
      // FIX 2026-05-21 (V40), stats temps réel par type de contrôle.
      {
        to: '/admin/control-stats',
        labelKey: 'adminNav.controlStats',
        icon: '📊',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.control_stats',
      },
      // FIX 2026-05-20 (sprint Plan Interactif Niveau 3) : CRUD des stands
      // du plan (49 zones V39 + 16 historiques V1).
      {
        to: '/admin/stands',
        labelKey: 'adminNav.stands',
        icon: '🗺️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.stands',
      },
      // 2026-09-09 (chantier C), éditeur de plan complet. Placé juste après
      // « Stands » : on y dessine ce que l'autre écran se contente de lister.
      {
        to: '/admin/plan-editor',
        labelKey: 'adminNav.planEditor',
        icon: '📐',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.stands',
      },
      // FIX 2026-05-25 (sprint borne), scripts kiosque téléchargeables
      // pour lancer Chrome plein écran sur la page de connexion borne
      // plastron (Windows/macOS/Linux) + instructions Android/iOS.
      {
        to: '/admin/kiosk-launchers',
        labelKey: 'adminNav.kioskLaunchers',
        icon: '🖥️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.kiosk_launchers',
      },
    ],
  },
  {
    titleKey: 'adminNav.group.system',
    items: [
      // FIX 2026-05-19 (journal log §5/§24), suivi opérationnel jour J
      // des connexions/actions organisateurs+exposants. Accessible à
      // TOUT rôle backoffice (≠ /admin/audit super_admin only).
      {
        to: '/admin/activity',
        labelKey: 'adminNav.activityLog',
        icon: '🛰️',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.activity_log',
      },
      // FIX 2026-05-20 (journal admin erreurs d'inscription §5/§24) :
      // suivi des user.register.*.fail (validation, email déjà utilisé,
      // token VIP expiré, code EXPO inconnu, etc.). Accessible
      // ALL_ADMIN_ROLES (pas de PII brute renvoyée par l'endpoint).
      // Pas de flag dédié, partage `admin.activity_log` puisque la
      // logique de monitoring opérationnel est identique.
      {
        to: '/admin/registration-errors',
        labelKey: 'adminNav.registrationErrors',
        icon: '🚧',
        roles: ALL_ADMIN_ROLES,
        flag: 'admin.activity_log',
      },
      {
        to: '/admin/maintenance',
        labelKey: 'adminNav.maintenance',
        icon: '🛠️',
        roles: SUPER_ADMIN_ONLY,
        // Pas de flag : un kill-switch sur la maintenance serait
        // contre-productif (anti-lockout système).
      },
      {
        to: '/admin/audit',
        labelKey: 'adminNav.audit',
        icon: '📜',
        roles: SUPER_ADMIN_ONLY,
        flag: 'admin.audit',
      },
      {
        to: '/admin/deploy-tokens',
        labelKey: 'adminNav.deployTokens',
        icon: '🚀',
        roles: SUPER_ADMIN_ONLY,
        // Pas de flag : sécurité-critique, piloté par deploy.enabled
        // côté serveur uniquement.
      },
      {
        to: '/admin/osnum-status',
        labelKey: 'adminNav.osnumStatus',
        icon: '🔗',
        roles: SUPER_ADMIN_ONLY,
        flag: 'admin.osnum_status',
      },
      // M5/G2 (2026-06-14, parité admin 2025), webhooks : abonnements HTTP
      // signés HMAC + file/retry/journal. super_admin only (secrets + appels
      // sortants). Flag de visibilité admin.webhooks (défaut ON).
      {
        to: '/admin/webhooks',
        labelKey: 'adminNav.webhooks',
        icon: '🪝',
        roles: SUPER_ADMIN_ONLY,
        flag: 'admin.webhooks',
      },
      // M5/G8 (2026-06-14, parité admin 2025), outil de fusion de 2 comptes
      // visiteurs en doublon. Destructif → super_admin only.
      {
        to: '/admin/merge-visitors',
        labelKey: 'adminNav.mergeVisitors',
        icon: '🔀',
        roles: SUPER_ADMIN_ONLY,
        flag: 'admin.merge_visitors',
      },
    ],
  },
  {
    titleKey: 'adminNav.group.rights',
    items: [
      {
        // NON disponible par defaut : seul le super-administrateur l'a
        // d'origine. On l'accorde explicitement dans /admin/access.
        to: '/admin/impersonate',
        labelKey: 'adminNav.right.impersonate',
        icon: '👁',
        roles: SUPER_ADMIN_ONLY,
        virtual: true,
      },
      {
        // 2026-09-30 (Mika), valider, refuser ou detacher les demandes de
        // rattachement d un compte exposant a une autre entreprise. Onglet
        // « Rattachements » de l ecran Exposants. NON disponible par defaut :
        // le super administrateur seul, attribuable a une personne ici.
        // Le serveur (api/admin/recruiter-links.php) porte la vraie garde.
        to: '/admin/recruiter-links',
        labelKey: 'adminNav.right.recruiterLinks',
        icon: '🤝',
        roles: SUPER_ADMIN_ONLY,
        virtual: true,
      },
      {
        // 2026-09-30 (Mika), activer, refuser ou retirer le premium d une
        // entreprise dans l ecran Exposants. Jusque-la ouvert a tout compte
        // qui voyait l ecran ; desormais super administrateur par defaut,
        // Mika l accorde a Laurent par surcharge de compte. Le serveur
        // (api/admin/premium.php) refuse aussi les ecritures sans ce droit.
        to: '/admin/premium-grant',
        labelKey: 'adminNav.right.premiumGrant',
        icon: '⭐',
        roles: SUPER_ADMIN_ONLY,
        virtual: true,
      },
      {
        // 2026-09-20, la fiche complete d'une personne. Virtuelle : elle ne
        // figure pas dans la barre laterale, on y arrive depuis les listes
        // (comptes, inscrits, exposants, animateurs). Mais le droit doit
        // etre attribuable dans /admin/access, car cette fiche montre 100 %
        // des informations d'une personne.
        to: '/admin/person',
        labelKey: 'adminNav.person',
        icon: '🪪',
        roles: ALL_ADMIN_ROLES,
        virtual: true,
      },
    ],
  },
  {
    // FIX 2026-08-26 (signale par Mika), « Modifier son mot de passe » avait
    // ete cree en droit VIRTUEL : attribuable dans /admin/access, mais sans
    // route ni ecran. Accorde a un moderateur, il ne faisait donc RIEN
    // apparaitre dans son menu. Un droit sans consommateur est un faux vert.
    // Devenu une vraie section : la V76 sait deja l'accorder par role ET par
    // compte, et la CLE `/admin/self-password` est conservee a l'identique
    // pour que les attributions DEJA faites restent valables.
    titleKey: 'adminNav.group.myAccount',
    items: [
      {
        // Ouvert par defaut : le RETIRER est le choix delibere.
        to: '/admin/self-password',
        labelKey: 'adminNav.right.selfPassword',
        icon: '🔑',
        roles: ALL_ADMIN_ROLES,
      },
      // 2026-09-20 (Mika), sa propre photo de profil, pour tout compte
      // d'administration. Ouverte par defaut, comme le mot de passe.
      {
        to: '/admin/self-photo',
        labelKey: 'adminNav.selfPhoto',
        icon: '🖼️',
        roles: ALL_ADMIN_ROLES,
      },
    ],
  },
  // ───────────────────────────────────────────────────────────────────────
  // MON COMPTE. Dernier groupe, volontairement separe des sections
  // d administration : ce qu on y regle ne concerne QUE le compte connecte.
  //
  // POURQUOI CE GROUPE EXISTE : la porte de sortie du dispositif, revenir au
  // code par courriel, doit etre atteignable sans taper une URL a la main.
  // Une porte de sortie sans poignee n est pas une porte de sortie.
  //
  // Roles = VIP_MANAGER_ALLOWED, donc TOUS les roles du back-office : chacun
  // gere sa propre double authentification.
  // ───────────────────────────────────────────────────────────────────────
  {
    titleKey: 'adminNav.group.account',
    items: [
      {
        to: '/admin/securite',
        labelKey: 'adminNav.securite',
        icon: '🔐',
        roles: VIP_MANAGER_ALLOWED,
      },
    ],
  },
] as const;

/**
 * Décide si une entrée doit s'afficher pour `role`, compte tenu des
 * rôles autorisés ET de l'état du flag (défaut ON : on masque
 * UNIQUEMENT si le flag est explicitement désactivé).
 *
 * @param item         L'entrée de menu.
 * @param role          Rôle backoffice courant, sous forme de chaîne
 *                       (un `AdminRole` ou une valeur libre du serveur ;
 *                       null = inconnu → on laisse passer le filtre rôle,
 *                       le RoleGuard tranchera).
 * @param disabledFlags Ensemble des clés de flag explicitement OFF.
 */
// FIX 2026-05-17 (Chantier A), `AdminRole | string` se réduisait à
// `string` (no-redundant-type-constituents). On garde `string | null`
// (AdminRole ⊂ string ; le narrowing `as AdminRole` interne est conservé,
// zéro changement de comportement).
export function isNavItemVisible(
  item: AdminNavItem,
  role: string | null,
  disabledFlags: ReadonlySet<string>,
): boolean {
  // Filtre rôle (UX, la sécurité réelle est le RoleGuard + serveur).
  // Un DROIT VIRTUEL n'est jamais une entree de menu : aucune page derriere.
  if (item.virtual === true) {
    return false;
  }
  if (item.tabOf !== undefined) {
    return false;
  }
  // Une section `grantable` laisse la decision a la resolution des droits
  // (AdminShell la filtre ensuite par `allowedSet`, surcharges comprises).
  if (role !== null && item.grantable !== true && !item.roles.includes(role as AdminRole)) {
    return false;
  }
  // Filtre flag : masqué SEULEMENT si explicitement désactivé.
  if (item.flag !== undefined && disabledFlags.has(item.flag)) {
    return false;
  }
  return true;
}

/** Liste plate de toutes les routes admin déclarées (utile aux tests). */
export const ADMIN_NAV_ROUTES: readonly string[] = ADMIN_NAV.flatMap((g) =>
  g.items.filter((i) => i.virtual !== true).map((i) => i.to),
);

/** Cles des droits virtuels (capacites attribuables, sans page). */
export const ADMIN_VIRTUAL_RIGHTS: readonly string[] = ADMIN_NAV.flatMap((g) =>
  g.items.filter((i) => i.virtual === true).map((i) => i.to),
);
